/**
 * Icon mapper utility for dynamic specification icons
 * Maps question keys/icon names to React icon components
 */
import React from 'react';
import {
  FiHardDrive,
  FiWifi,
  FiBatteryCharging,
  FiAward,
  FiTool,
  FiMonitor,
  FiBox,
  FiCalendar,
  FiCpu,
  FiServer,
  FiPackage,
  FiClock,
  FiHash,
  FiTag,
  FiSmartphone,
  FiCheck,
  FiSettings,
  FiZap,
  FiShield,
  FiLayers,
} from 'react-icons/fi';
import { Smartphone, Tablet, Watch, Monitor, Laptop, Gamepad2 } from 'lucide-react';

// Icon size constants
const ICON_SIZE = 18;
const ICON_SMALL = 16;

// Type for icon component
type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

/**
 * Maps question keys or icon names to their corresponding icon components
 */
const iconMap: Record<string, IconComponent> = {
  // Storage related
  storage: FiHardDrive,
  capacity: FiHardDrive,
  opslag: FiHardDrive,

  // Network related
  networkType: FiWifi,
  network: FiWifi,
  cellular: FiWifi,
  cellularSupport: FiWifi,
  wifi: FiWifi,

  // Battery related
  battery: FiBatteryCharging,
  batteryCapacity: FiBatteryCharging,
  batteryHealth: FiBatteryCharging,
  batterij: FiBatteryCharging,

  // Condition related
  condition: FiAward,
  conditie: FiAward,
  staat: FiAward,

  // Functionality related
  functionality: FiTool,
  functionaliteit: FiTool,
  werking: FiTool,

  // Screen related
  screenCondition: FiMonitor,
  screen: FiMonitor,
  scherm: FiMonitor,
  screenSize: FiMonitor,
  display: FiMonitor,

  // Housing/body related
  housingCondition: FiBox,
  housing: FiBox,
  behuizing: FiBox,
  body: FiBox,

  // Date/year related
  modelYear: FiCalendar,
  year: FiCalendar,
  jaar: FiCalendar,
  date: FiCalendar,

  // Processor related
  processor: FiCpu,
  cpu: FiCpu,
  chip: FiCpu,

  // RAM related
  ram: FiServer,
  memory: FiServer,
  geheugen: FiServer,

  // Accessories related
  accessories: FiPackage,
  accessoires: FiPackage,
  extras: FiPackage,

  // Watch related
  watchSize: FiClock,
  watchBand: FiPackage,
  horloge: FiClock,

  // Quantity related
  quantity: FiHash,
  aantal: FiHash,

  // Price related
  price: FiTag,
  prijs: FiTag,

  // Device related
  device: FiSmartphone,
  model: FiSmartphone,
  toestel: FiSmartphone,

  // Status/check related
  status: FiCheck,
  check: FiCheck,

  // Settings related
  settings: FiSettings,
  instellingen: FiSettings,

  // Power related
  power: FiZap,
  energie: FiZap,

  // Protection related
  protection: FiShield,
  bescherming: FiShield,
  warranty: FiShield,
  garantie: FiShield,

  // Layers/version related
  version: FiLayers,
  versie: FiLayers,
};

/**
 * Get the icon component for a given question key or icon name
 * @param key - The question key or icon name
 * @param size - Optional icon size (default: 18)
 * @returns React element with the icon, or null if no matching icon
 */
export const getSpecIcon = (key: string, size: number = ICON_SIZE): React.ReactNode => {
  // Normalize the key to lowercase for case-insensitive matching
  const normalizedKey = key.toLowerCase();

  // Try exact match first
  const IconComponent = iconMap[key] || iconMap[normalizedKey];

  if (IconComponent) {
    return <IconComponent size={size} />;
  }

  // Try partial matching for compound keys
  for (const [mapKey, Icon] of Object.entries(iconMap)) {
    if (normalizedKey.includes(mapKey.toLowerCase()) || mapKey.toLowerCase().includes(normalizedKey)) {
      return <Icon size={size} />;
    }
  }

  // Return wrench icon as fallback for unrecognized keys
  return <FiTool size={size} />;
};

/**
 * Get the icon component class for a given question key
 * Useful when you need the component itself rather than a rendered element
 */
export const getSpecIconComponent = (key: string): IconComponent | null => {
  const normalizedKey = key.toLowerCase();
  return iconMap[key] || iconMap[normalizedKey] || null;
};

/**
 * Check if an icon exists for the given key
 */
export const hasIcon = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return !!(iconMap[key] || iconMap[normalizedKey]);
};

/**
 * Get a device category icon (lucide) for the device grid cards
 */
export const getDeviceIcon = (categoryOrId: string): React.ReactNode => {
  const key = categoryOrId.toLowerCase();

  if (key.includes('phone') || key.includes('smartphone') || key === 'phone') {
    return <Smartphone strokeWidth={1.5} />;
  }
  if (key.includes('tablet') || key.includes('ipad')) {
    return <Tablet strokeWidth={1.5} />;
  }
  if (key.includes('watch') || key.includes('horloge')) {
    return <Watch strokeWidth={1.5} />;
  }
  if (key.includes('laptop') || key.includes('macbook')) {
    return <Laptop strokeWidth={1.5} />;
  }
  if (key.includes('console') || key.includes('gaming') || key.includes('gamepad')) {
    return <Gamepad2 strokeWidth={1.5} />;
  }
  if (key.includes('desktop') || key.includes('imac') || key.includes('monitor')) {
    return <Monitor strokeWidth={1.5} />;
  }

  return <Smartphone strokeWidth={1.5} />;
};

export default getSpecIcon;
